import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { authedPost } from '@/lib/api';

export interface ChartOfAccountsTemplate {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  organization_id: string | null; // NULL = global/system template
}

/** Full template row used by the system-settings admin grid. */
export interface ChartOfAccountsTemplateRow extends Record<string, unknown> {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
}

/** Template account row used by the system-settings admin grid. */
export interface ChartOfAccountsTemplateAccountRow extends Record<string, unknown> {
  id: string;
  template_id: string;
  /**
   * Sub-account link within the same template. `account_level` is DERIVED from
   * it by `coa_template_account_derive_hierarchy()` — the grid never sends a
   * level, which is what lets it satisfy the table's
   * `chart_of_accounts_template_parent_child_consistency` CHECK.
   */
  parent_id: string | null;
  account_level: number;
  account_number: string | null;
  account_name: string;
  account_description: string | null;
  category_id: string;
  type_id: string;
  subtype_id: string | null;
  normal_balance: 'debit' | 'credit';
  requires_project: boolean;
  sort_order: number;
}

// All template queries share the 'chartOfAccountsTemplates' prefix so a
// single prefix invalidation refreshes the org-creation picker AND the
// admin grids together.
export const COA_TEMPLATES_KEY_PREFIX = ['chartOfAccountsTemplates'];
export const SYSTEM_COA_TEMPLATES_KEY = ['chartOfAccountsTemplates', 'system'];
export const SYSTEM_COA_TEMPLATE_ACCOUNTS_KEY = ['chartOfAccountsTemplateAccounts', 'system'];

// Hono backend base for chart-of-accounts writes (mirrors useChartOfAccounts.ts).
const BASE = '/api/v1/chart-of-accounts';

/**
 * Available chart-of-accounts templates: global/system templates plus any
 * org-specific ones visible to the user (RLS handles scoping).
 */
export const useChartOfAccountsTemplates = () => {
  return useQuery({
    queryKey: COA_TEMPLATES_KEY_PREFIX,
    queryFn: async (): Promise<ChartOfAccountsTemplate[]> => {
      const { data, error } = await supabase
        .from('chart_of_accounts_templates')
        .select('id, name, description, is_default, organization_id')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
    // Rarely-changing reference data (admin-curated templates).
    staleTime: 30 * 60 * 1000,
  });
};

/**
 * All global/system templates (incl. inactive) for the system-settings
 * admin grid. Super-admin gated by the page; RLS scopes reads.
 */
export const useSystemCoaTemplates = () => {
  return useQuery({
    queryKey: SYSTEM_COA_TEMPLATES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts_templates')
        .select('id, name, description, is_active, is_default, sort_order')
        .is('organization_id', null)
        .order('sort_order')
        .order('name');

      if (error) throw error;
      return data as ChartOfAccountsTemplateRow[];
    },
  });
};

/** All accounts of all global templates, one query — sectioned client-side. */
export const useSystemCoaTemplateAccounts = () => {
  return useQuery({
    queryKey: SYSTEM_COA_TEMPLATE_ACCOUNTS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts_template_accounts')
        .select(
          'id, template_id, parent_id, account_level, account_number, account_name, account_description, category_id, type_id, subtype_id, normal_balance, requires_project, sort_order, chart_of_accounts_templates!inner(organization_id)'
        )
        .is('chart_of_accounts_templates.organization_id', null)
        .order('sort_order');

      if (error) throw error;
      return (data ?? []).map(
        ({ chart_of_accounts_templates: _t, ...row }) => row
      ) as ChartOfAccountsTemplateAccountRow[];
    },
  });
};

/**
 * Invalidates every template query (picker + admin grids) after a write.
 * The shared 'chartOfAccountsTemplates' prefix covers the picker and the
 * admin template list in one call.
 */
export const useInvalidateCoaTemplates = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: COA_TEMPLATES_KEY_PREFIX });
    queryClient.invalidateQueries({ queryKey: SYSTEM_COA_TEMPLATE_ACCOUNTS_KEY });
  }, [queryClient]);
};

interface ApplyTemplateResult {
  success: boolean;
  template_id: string;
  inserted_count: number;
  skipped_count: number;
}

/**
 * Applies a template to an organization via the apply_chart_of_accounts_template
 * RPC (idempotent — accounts that already exist for the org are skipped).
 *
 * Routed through the Hono backend: the RPC is SECURITY INVOKER and INSERTs into
 * chart_of_accounts, which is backend-only (RESTRICTIVE verify_backend_token()
 * INSERT policy). A direct supabase.rpc() call from the browser carries no HMAC
 * and is rejected by RLS; the backend's per-request client attaches it.
 *
 * No onError toast here: callers handle errors so the org-creation flow can
 * distinguish "org created but template failed" from a hard failure.
 */
export const useApplyChartOfAccountsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, templateId }: { organizationId: string; templateId: string }) =>
      authedPost<ApplyTemplateResult>(
        `${BASE}/orgs/${organizationId}/apply-template`,
        { template_id: templateId },
      ),
    onSuccess: (_data, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
    },
  });
};
